import React from "react";
import { Row, Col, Button } from "antd";
import { ColumnsType, ColumnGroupType, ColumnType } from "antd/es/table";
import styles from "./ServicesTable.module.scss";
import textStyles from "../../Shared/Styles/TextStyles.module.scss";
import buttonStyles from "../../Shared/Styles/ButtonStyles.module.scss";
import moment from "moment";
import getTripIcon from "../../../helpers/getTripIcon";
import vertical3DotIcon from "../../../assets/icons/moreOutlined.svg";
import sortIcon from "../../../assets/icons/sort.svg";
import cancelledIcon from "../../../assets/icons/cancelAvatar.svg";
import rightOutlinedIcon from "../../../assets/icons/rightOutlined.svg";
import { WandaTripStatus } from "../../../types/ApplicationTypes";
import { ITripDetails } from "../../ServiceUsers/Interfaces/ITripDetails";
import { ActionHandlerFunctions } from "../TripDetails/TripDetailsDrawer";
import { Tabs } from "../../../types/TripPaginationFilterTypes";
import DropdownActions from "./DropdownActions";

type TripsColumnType = {
  key?: React.Key;
  tripDate?: string;
  serviceUser?: string;
  tripCost?: number;
  tripReason?: string;
  favoriteName?: string;
};

type ColumnHandlers = {
  activeTab: string;
  handleSortToggle: () => void;
  handleServiceUser: (personId: number) => void;
  handleTripDetails: (record: ITripDetails) => void;
  shouldHideColumn: (
    column: ColumnGroupType<TripsColumnType> | ColumnType<TripsColumnType>
  ) => boolean;
  actionHandlers: ActionHandlerFunctions;
};

export function getServiceColumns({
  activeTab,
  handleSortToggle,
  handleServiceUser,
  handleTripDetails,
  shouldHideColumn,
  actionHandlers,
}: ColumnHandlers) {
  const upcomingAndPastColumns: ColumnsType<TripsColumnType> = [
    {
      title: "",
      key: "tripStatus",
      width: "6.46%",
      render: (record) =>
        record.tripStatusId === WandaTripStatus.Cancelled && (
          <img src={cancelledIcon} alt="cancelled_service" />
        ),
    },
    {
      title: (
        <Row
          justify="space-between"
          className={styles.columnHeaderAlign}
          onClick={handleSortToggle}
        >
          <Col>Time/Date</Col>
          <Col>
            <img src={sortIcon} alt="sort_time_date" />
          </Col>
        </Row>
      ),
      key: "tripDate",
      width: "23.61%",
      render: (record): JSX.Element => (
        <Row>
          <Col span={24} className={textStyles.normalText14}>
            {record.isWillCall
              ? "Call when ready"
              : `${moment(
                  record.appointmentTime || record.requestPickTime
                ).format("hh:mm A")} ${record.sourceSystemLocation}`}
          </Col>
          <Col span={24} className={textStyles.tertiaryNormalText14}>
            {moment(record.tripDate).format("MM/DD/YYYY")}
          </Col>
        </Row>
      ),
    },
    {
      title: "Service User",
      key: "serviceUser",
      width: "21.22%",
      ellipsis: true,
      render: (record) => (
        <Button
          type="link"
          className={`${buttonStyles.underlinedLinkBtn} ${textStyles.normalText14}`}
          onClick={() => handleServiceUser(record.personId)}
        >
          {record.serviceUser}
        </Button>
      ),
    },
    {
      title: <Row justify="end">Cost</Row>,
      key: "tripCost",
      render: (record): JSX.Element => (
        <Row
          justify="end"
          className={`${
            record.tripStatusId === WandaTripStatus.Cancelled
              ? textStyles.disabledNormalText14
              : textStyles.normalText14
          }`}
        >
          ${record.tripCost?.toFixed(2)}
        </Row>
      ),
      width: "15.34%",
    },
    {
      title: "Type",
      key: "tripReason",
      width: "14.45%",
      render: (record): JSX.Element => (
        <Row className={textStyles.normalText14} align="middle" gutter={[8, 0]}>
          <Col>{getTripIcon(record.tripReason)}</Col>
        </Row>
      ),
    },
    {
      title: <Row justify="center">Actions</Row>,
      width: "11.57%",
      key: "actions",
      render: (record) => (
        <Row justify="center">
          <DropdownActions selectedLeg={record} actionHandlers={actionHandlers}>
            <Button className={buttonStyles.iconBtn}>
              <img src={vertical3DotIcon} alt="actions" />
            </Button>
          </DropdownActions>
        </Row>
      ),
    },
    {
      title: "",
      width: "7.81%",
      render: (record) => (
        <Row justify="center">
          <Button
            className={buttonStyles.iconBtn}
            onClick={() => handleTripDetails(record)}
          >
            <img src={rightOutlinedIcon} alt="tripDetails" />
          </Button>
        </Row>
      ),
    },
  ];

  const favoriteColumns: ColumnsType<TripsColumnType> = [
    {
      title: "",
      width: "4.46%",
    },
    {
      title: <Row>Time</Row>,
      key: "tripDate",
      width: "22.38%",
      render: (record): JSX.Element => (
        <Row className={textStyles.normalText14}>
          {record.isWillCall
            ? "Call when ready"
            : `${moment(
                record.appointmentTime || record.requestPickTime
              ).format("hh:mm A")} ${record.sourceSystemLocation}`}
        </Row>
      ),
    },
    {
      title: (
        <Row
          justify="space-between"
          className={styles.columnHeaderAlign}
          onClick={handleSortToggle}
        >
          <Col>Name</Col>
          <Col>
            <img src={sortIcon} alt="sort_time_date" />
          </Col>
        </Row>
      ),
      key: "favoriteName",
      width: "18.13%",
      ellipsis: true,
      render: (record) => (
        <Row className={styles.normalText14}>{record?.favoriteName}</Row>
      ),
    },
    {
      title: "Service User",
      key: "serviceUser",
      width: "16.45%",
      ellipsis: true,
      render: (record) => (
        <Button
          type="link"
          className={`${buttonStyles.underlinedLinkBtn} ${textStyles.normalText14}`}
          onClick={() => handleServiceUser(record.personId)}
        >
          {record.serviceUser}
        </Button>
      ),
    },
    {
      title: "Type",
      key: "tripReason",
      width: "12.76%",
      render: (record): JSX.Element => (
        <Row className={textStyles.normalText14} align="middle" gutter={[8, 0]}>
          <Col>{getTripIcon(record.tripReason)}</Col>
        </Row>
      ),
    },
    {
      title: <Row justify="center">Actions</Row>,
      width: "11.57%",
      key: "actions",
      render: (record) => (
        <Row justify="center">
          <DropdownActions selectedLeg={record} actionHandlers={actionHandlers}>
            <Button className={buttonStyles.iconBtn}>
              <img src={vertical3DotIcon} alt="actions" />
            </Button>
          </DropdownActions>
        </Row>
      ),
    },
    {
      title: "",
      width: "7.81%",
      render: (record) => (
        <Button
          className={buttonStyles.iconBtn}
          onClick={() => handleTripDetails(record)}
        >
          <img src={rightOutlinedIcon} alt="tripDetails" />
        </Button>
      ),
    },
  ];

  return activeTab === Tabs.FAVORITE
    ? favoriteColumns.filter(shouldHideColumn)
    : upcomingAndPastColumns.filter(shouldHideColumn);
}
